import React, { useEffect, useState, useRef, useCallback } from "react";
import { debounce } from "lodash";
import styled from "styled-components";

import client from "@culturehq/client";
import { SelectField, Icon, Tooltip } from "@culturehq/components";

import { useProfile } from "app/context";
import { getDepartmentFieldCategory, getCompanyFieldCategory, isDeloitte, isMicrosoft, isRTX, t } from "app/utils";
import useTags from "components/post/useTags";
import tagsTypes from "constants/tagsTypes";
import getUserName from "app/utils/getUserName";

const StyledSelectField = styled(SelectField)`
  .chq-ffd--sl {
    .chq-ffd--ctrl {
      flex-wrap: ${({ withContent }) => `${withContent ? "wrap" : "nowrap"}`} !important;
    }
  }
`;

const SelectTags = ({
  ariaLabel = "",
  children = undefined,
  normalDesign = true,
  onShareWithChanged,
  placeholder,
  selectedOptions,
  setEditorPadding,
  isSearchBar = false,
  optionsCallback = undefined,
  withUsers = false,
  withOrgValues = false,
  wholeCompany = false,
  creatable = true,
  storyQuestions = [],
  creatableLabel = t("Create a tag"),
  newOptions = [],
  allWordsMatch = false,
  icon = undefined
}) => {
  const { profile: { organization: { name } } } = useProfile();
  const [addedOptions, setAddedOptions] = useState([]);
  const [tags, setTags] = useState([]);
  const [display, setDisplay] = useState("");
  const selectRef = useRef(null);
  const hasMounted = useRef(true);

  const height = selectRef.current?.offsetHeight;
  const SEARCH_DELAY = 500;

  const { options: selectOptions, indexes } = useTags(
    optionsCallback, withOrgValues, wholeCompany, storyQuestions, addedOptions
  );

  useEffect(() => (
    () => {
      hasMounted.current = false;
    }
  ), []);

  useEffect(
    () => {
      if (!newOptions.length) return;

      const filteredOptions = [...addedOptions, ...newOptions].reduce((acc, opt) => {
        const exists = !!acc.find(o => o.value === opt.value);
        if (!exists) acc.push(opt);
        return acc;
      }, []);

      setAddedOptions(filteredOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newOptions]
  );

  useEffect(() => {
    if (!hasMounted.current) return undefined;

    const timeout = setTimeout(() => {
      if (selectedOptions.length > 0 && setEditorPadding) {
        setEditorPadding({ paddingBottom: `${height + 10}px` });
      }
    }, 50); // Debounce slightly

    if (name === "Dartmouth Guarini") {
      setTags(selectedOptions.map(option => {
        if (option.category === tagsTypes.CLASS_YEARS) {
          return { ...option, category: tagsTypes.YEARS_IN_PROGRAM };
        }
        return option;
      }));
    } else if (isDeloitte(name) || isMicrosoft(name) || isRTX(name)) {
      setTags(selectedOptions.map(option => {
        if (option.category === tagsTypes.DEPARTMENTS) {
          return { ...option, category: getDepartmentFieldCategory(name) };
        }
        if (option.category === tagsTypes.COMPANIES) {
          return { ...option, category: getCompanyFieldCategory(name) };
        }
        return option;
      }));
    } else {
      setTags(selectedOptions);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [selectedOptions, setEditorPadding, height, name]);

  const getSelectedObjects = values => {
    const results = [];
    values.forEach(element => {
      switch (element.category) {
        case tagsTypes.INTERESTS:
          results.push({ ...element, label: indexes.interests[element.value].name, categoryIcon: "heart-ol" });
          break;
        case tagsTypes.LOCATIONS:
          results.push({ ...element, label: indexes.locations[element.value].name, categoryIcon: "loc-ol" });
          break;
        case tagsTypes.COMPANIES:
          results.push({ ...element, label: indexes.companies[element.value].name, categoryIcon: "building" });
          break;
        case tagsTypes.BUSINESSES:
        case tagsTypes.PROFESSIONS:
        case tagsTypes.JOBFUNCTIONS:
        case tagsTypes.DEPARTMENTS:
          if (isRTX(name) && tagsTypes.BUSINESSES) {
            results.push({ ...element, label: indexes.companies[element.value].name, categoryIcon: "building", category: tagsTypes.COMPANIES });
          } else {
            results.push({ ...element, label: indexes.departments[element.value].name, categoryIcon: "ios-people-outline" });
          }
          break;
        case tagsTypes.SKILLS:
          results.push({ ...element, label: indexes.skills[element.value].name, categoryIcon: "hammer" });
          break;
        case tagsTypes.PROGRAMS:
          results.push({ ...element, label: indexes.programs[element.value].name, categoryIcon: "ios-star-outline" });
          break;
        case tagsTypes.DEGREES:
          results.push({ ...element, label: indexes.degrees[element.value].name, categoryIcon: "mortarboard" });
          break;
        case tagsTypes.CLUBS:
          results.push({ ...element, label: indexes.clubs[element.value].name, categoryIcon: "ios-people-outline" });
          break;
        case tagsTypes.YEARS_IN_PROGRAM:
          results.push({ ...element, label: indexes.classYears[element.value].name, categoryIcon: "clock" });
          break;
        case tagsTypes.CLASS_YEARS:
          results.push({ ...element, label: indexes.classYears[element.value].name, categoryIcon: "clock" });
          break;
        case tagsTypes.USERS:
          results.push({
            ...element,
            label: selectOptions.find(option => option.value === element.value)?.label
          });
          break;
        case tagsTypes.ORG_VALUES:
          results.push({ ...element, label: indexes.organizationValues[element.value].name, categoryIcon: "diamond" });
          break;
        case tagsTypes.ALL_ORGANIZATION:
          results.push({ ...element });
          break;
        case tagsTypes.STORY_PROMPTS:
        case tagsTypes.STORY_TRENDS:
          results.push({
            ...element,
            label: indexes.storyPrompts[element.value].question,
            categoryIcon: "book"
          });
          break;
        default:
          // If is a new options
          if (typeof element === "string") {
            results.push(element);
            break;
          }

          // This is when you edit the post, you have options without category
          results.push(selectedOptions.find(opt => opt.value === element));
          break;
      }
    });

    return results;
  };

  const performSearch = async query => {
    const { entities } = await client.autocompleteMentions({ query });
    const users = entities.filter(entity => (
      entity.type === "users" && !addedOptions.some(option => option.value === entity.id)
    ));
    setAddedOptions([...addedOptions, ...users.map(user => (
      {
        category: tagsTypes.USERS,
        categoryIcon: "user",
        icon: "user-filled",
        label: getUserName(user),
        value: user.id
      }
    ))]);
  };

  const debouncedEnqueueSearch = useCallback(debounce(performSearch, SEARCH_DELAY), [addedOptions]);

  const handleTextChange = value => {
    if (withUsers) {
      setDisplay(value);
      debouncedEnqueueSearch(value);
    }
  };

  const onSelectedGroupsChange = value => {
    setTags(value);
    setDisplay("");
    onShareWithChanged(getSelectedObjects(value), selectRef.current.offsetHeight);
  };

  return (
    <div className="Select-Tags__Container" ref={selectRef}>
      <StyledSelectField
        withContent={!!selectedOptions.length}
        allWordsMatch={allWordsMatch}
        ariaLabel={ariaLabel}
        className="share-with-field"
        creatable={creatable}
        creatableLabel={creatableLabel}
        initialDisplay={display}
        name="allOptions"
        options={selectOptions}
        placeholder={placeholder}
        multiple
        onChange={onSelectedGroupsChange}
        onTextChanged={handleTextChange}
        value={tags}
        removePlacholder={isSearchBar}
      >
        {isSearchBar && <Icon icon={icon || "search"} />}
        {normalDesign && !isSearchBar && <Icon icon="tags" />}
        {normalDesign && !isSearchBar && (
          <Tooltip tip={t("Select one or more groups to share this post in their 'For You' feed. All users may view it in the 'All Activity' feed.")}>
            <Icon className="info-icon" icon="info-thinner" />
          </Tooltip>
        )}
        {children}
      </StyledSelectField>
    </div>
  );
};

export default SelectTags;
